import { PolicyInputType } from "../types/builder";

export type FormStatePathSegment = string | number;

export function normalizeFieldValue(
  value: unknown,
  inputType?: PolicyInputType,
): unknown {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string | number =>
        typeof item === "string" || typeof item === "number",
    );
  }
  if (typeof value === "boolean" || typeof value === "number") {
    return value;
  }
  if (value === null || value === undefined) {
    if (inputType === "MULTI_SELECT") {
      return [];
    }
    if (inputType === "SWITCH") {
      return false;
    }
    return "";
  }
  return String(value);
}

export function isEmptyPolicyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === "boolean") {
    return false;
  }
  if (typeof value === "number") {
    return Number.isNaN(value);
  }
  return false;
}

const PATH_SEGMENT_RE = /([^[.\]]+)|\[(\d+)\]/g;

export function parseFormStatePath(path: string): FormStatePathSegment[] {
  const segments: FormStatePathSegment[] = [];
  let match: RegExpExecArray | null;

  PATH_SEGMENT_RE.lastIndex = 0;
  while ((match = PATH_SEGMENT_RE.exec(path)) !== null) {
    if (match[1] !== undefined) {
      segments.push(match[1]);
    } else if (match[2] !== undefined) {
      segments.push(Number(match[2]));
    }
  }

  return segments;
}

export function joinFormStatePath(scope: string, relativePath: string): string {
  if (!scope) {
    return relativePath;
  }
  if (!relativePath) {
    return scope;
  }
  return `${scope}.${relativePath}`;
}

/** Alias used by builder mapper / field path registry. */
export const joinFormDataPath = joinFormStatePath;

export function indexFormStatePath(arrayPath: string, index: number): string {
  return `${arrayPath}[${index}]`;
}

export function joinFormStatePathSegments(
  segments: FormStatePathSegment[],
): string {
  return segments.reduce<string>((path, segment) => {
    if (typeof segment === "number") {
      return `${path}[${segment}]`;
    }
    return joinFormStatePath(path, segment);
  }, "");
}

export function formStatePath(path: string): string[] {
  const segments = parseFormStatePath(path);
  const chain: string[] = [];

  for (let length = segments.length; length >= 1; length -= 1) {
    chain.push(joinFormStatePathSegments(segments.slice(0, length)));
  }

  return chain;
}

export function getFormStateValue(
  root: Record<string, unknown>,
  path: string,
): unknown {
  let current: unknown = root;

  for (const segment of parseFormStatePath(path)) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = (current as Record<FormStatePathSegment, unknown>)[segment];
  }

  return current;
}

export function setFormStateValue(
  root: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const segments = parseFormStatePath(path);
  let current: Record<FormStatePathSegment, unknown> = root;

  segments.forEach((segment, position) => {
    const isLast = position === segments.length - 1;

    if (isLast) {
      current[segment] = value;
      return;
    }

    const existing = current[segment];
    if (existing === null || existing === undefined) {
      const nextSegment = segments[position + 1];
      current[segment] = typeof nextSegment === "number" ? [] : {};
    }

    current = current[segment] as Record<FormStatePathSegment, unknown>;
  });
}

export function getRuleCategoryScope(categoryKey: string): string {
  return `ruleCategories.${categoryKey}`;
}

export function subsectionKeyToDataPath(subSectionKey: string): string {
  return subSectionKey.toLowerCase();
}
